import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ingestMeasure, lookupBill } from "@/lib/api";
import { ArrowRight, Link as LinkIcon, Plus, Trash2, Loader2, FileText, AlertTriangle, Scale, Search, ListChecks } from "lucide-react";
import StartHerePanel from "@/components/StartHerePanel";

const SAMPLES = [
  {
    label: "CA Prop 36 (2024) — Official Voter Guide text",
    url: "https://voterguide.sos.ca.gov/propositions/36/",
  },
  {
    label: "CA Prop 5 (2024) — LAO analysis",
    url: "https://lao.ca.gov/BallotAnalysis/Proposition?number=5&year=2024",
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [electionDate, setElectionDate] = useState("");
  const [urls, setUrls] = useState([""]);
  const [pastedText, setPastedText] = useState("");
  const [loading, setLoading] = useState(false);

  // Bill lookup state
  const [billQuery, setBillQuery] = useState("");
  const [billSession, setBillSession] = useState("");
  const [billLoading, setBillLoading] = useState(false);

  const addUrl = () => setUrls((u) => [...u, ""]);
  const setUrl = (i, v) => setUrls((u) => u.map((x, j) => (j === i ? v : x)));
  const removeUrl = (i) => setUrls((u) => u.filter((_, j) => j !== i));

  const submitBill = async (e) => {
    if (e) e.preventDefault();
    const q = billQuery.trim();
    if (!q) {
      toast.error("Enter a bill number, e.g., 'AB 1234'.");
      return;
    }
    setBillLoading(true);
    try {
      const data = await lookupBill({
        query: q,
        session: billSession.trim() || undefined,
      });
      toast.success(`Fetched ${data.measure_id}`);
      navigate(`/measures/${data.id}`);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || "Bill lookup failed";
      toast.error(detail);
    } finally {
      setBillLoading(false);
    }
  };

  const submit = async () => {
    const cleanUrls = urls.map((u) => u.trim()).filter(Boolean);
    if (!cleanUrls.length && !pastedText.trim()) {
      toast.error(
        "A measure name alone isn't enough to analyze. Add at least one official URL (e.g., voterguide.sos.ca.gov or LAO) or paste the full measure text below."
      );
      return;
    }
    setLoading(true);
    try {
      const data = await ingestMeasure({
        title: title.trim() || undefined,
        election_date: electionDate.trim() || undefined,
        urls: cleanUrls,
        pasted_text: pastedText.trim() || undefined,
        pasted_title: title.trim() || undefined,
      });
      toast.success(`Ingested: ${data.measure_id}`);
      navigate(`/measures/${data.id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Ingest failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-16" data-testid="home-page">
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-5">
          <span className="stamp stamp-explicit">California · Phase 1-2 MVP</span>
          <span className="stamp stamp-implied">Evidence-first</span>
        </div>

        <h1 className="hero-title text-4xl sm:text-5xl lg:text-6xl text-[hsl(var(--ink))] mb-5">
          Read the ballot the way a{" "}
          <span className="italic">hostile contract reviewer</span> would.
        </h1>

        <p className="text-base sm:text-lg text-[hsl(var(--ink-2))] max-w-2xl leading-relaxed">
          Measure X-Ray decodes California bills and ballot measures &mdash; strips campaign gloss,
          surfaces buried provisions, compares proposed text against current law side-by-side, and cites every
          claim back to the source.
          <span className="block mt-2 text-sm text-[hsl(var(--ink-muted))]">
            It never tells you how to vote.
          </span>
        </p>
      </section>

      <StartHerePanel />

      {/* ---------- BROWSE BALLOT MEASURES ---------- */}
      <section
        className="paper-card-elevated rounded-sm p-5 sm:p-7 mb-5"
        data-testid="browse-measures-card"
      >
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-dashed border-[hsl(var(--rule))]">
          <div>
            <div className="section-label">Browse Ballot Measures</div>
            <div className="text-xs text-[hsl(var(--ink-muted))] mt-1 max-w-2xl">
              Pick from the official California Secretary of State ballot-measure
              list. Measure X-Ray will choose the best official text source for
              analysis.
            </div>
          </div>
          <ListChecks className="w-5 h-5 text-[hsl(var(--ink-muted))]" />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-[hsl(var(--ink-muted))] font-mono">
            Pulled live from sos.ca.gov · Qualified + Eligible measures
          </div>
          <button
            type="button"
            data-testid="browse-measures-cta"
            onClick={() => navigate("/measures/browse")}
            className="pill-btn pill-btn-primary justify-center"
          >
            Browse measures <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ---------- SESSION WARNING (above bill lookup) ---------- */}
      <div
        className="paper-card rounded-sm p-3 sm:p-4 mb-3 border-l-4 border-amber-600 bg-amber-50/50"
        data-testid="session-warning"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <p className="text-xs sm:text-sm text-[hsl(var(--ink))] leading-relaxed">
            <strong className="font-semibold">Important:</strong> Bill numbers
            are reused across legislative sessions. The session year matters.
            For example, AB 130 from one session may be a completely different
            bill than AB 130 from another session. Make sure you choose or
            enter the correct session year so Measure X-Ray analyzes the bill
            you actually mean.{" "}
            <span className="text-[hsl(var(--ink-2))]">
              If you leave the session field blank, Measure X-Ray will default
              to bills from the <strong>current legislative session</strong>.
            </span>
          </p>
        </div>
      </div>

      {/* ---------- BILL LOOKUP ---------- */}
      <section className="paper-card-elevated rounded-sm p-5 sm:p-7 mb-5" data-testid="bill-lookup">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-dashed border-[hsl(var(--rule))]">
          <div>
            <div className="section-label">Assembly / Senate Bill Lookup</div>
            <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">
              Enter a CA bill number. We&rsquo;ll pull it from leginfo and analyze it.
            </div>
          </div>
          <Scale className="w-5 h-5 text-[hsl(var(--ink-muted))]" />
        </div>

        <form onSubmit={submitBill} className="flex items-stretch gap-2 flex-col sm:flex-row">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-muted))]" />
            <input
              data-testid="bill-query-input"
              value={billQuery}
              onChange={(e) => setBillQuery(e.target.value)}
              placeholder="AB 1234  ·  SB 567  ·  ACA 2"
              className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm pl-9 pr-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue))]"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <input
            data-testid="bill-session-input"
            value={billSession}
            onChange={(e) => setBillSession(e.target.value)}
            placeholder="Session (e.g. 20252026)"
            className="w-full sm:w-48 bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue))]"
          />
          <button
            type="submit"
            data-testid="bill-lookup-submit"
            disabled={billLoading}
            className="pill-btn pill-btn-primary disabled:opacity-50 justify-center"
          >
            {billLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Looking up…
              </>
            ) : (
              <>
                Fetch &amp; analyze <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <div className="mt-3 flex flex-wrap gap-2">
          {["AB 1234", "SB 1", "AB 2013", "SB 10"].map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setBillQuery(q)}
              data-testid={`bill-sample-${q.replace(/\s/g, "-")}`}
              className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))] px-2 py-1 border border-[hsl(var(--rule))] rounded-sm transition-colors"
            >
              {q}
            </button>
          ))}
          <span className="text-[11px] text-[hsl(var(--ink-muted))] font-mono self-center">
            · defaults to current session
          </span>
        </div>
      </section>

      {/* ---------- DIVIDER ---------- */}
      <div className="flex items-center gap-3 mb-5 text-[hsl(var(--ink-muted))]">
        <div className="flex-1 h-px bg-[hsl(var(--rule))]" />
        <span className="text-[11px] font-mono uppercase tracking-[0.18em]">or paste your own source</span>
        <div className="flex-1 h-px bg-[hsl(var(--rule))]" />
      </div>

      <section className="paper-card-elevated rounded-sm p-5 sm:p-7" data-testid="ingest-form">
        <div className="flex items-center justify-between mb-5 pb-3 border-b border-dashed border-[hsl(var(--rule))]">
          <div>
            <div className="section-label">Exhibit A — Source Intake</div>
            <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">
              Official California sources preferred. PDFs and HTML supported.
            </div>
          </div>
          <FileText className="w-5 h-5 text-[hsl(var(--ink-muted))]" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <label className="block">
            <span className="section-label block mb-1.5">Scan label (for your reference)</span>
            <input
              data-testid="input-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Prop 36"
              className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue))]"
            />
            <p className="text-xs text-[hsl(var(--ink-muted))] mt-1.5 leading-snug">
              Label only names this scan. To analyze a ballot measure, add at least one official URL or paste the full measure text below.
            </p>
          </label>
          <label className="block">
            <span className="section-label block mb-1.5">Election (optional)</span>
            <input
              data-testid="input-election"
              value={electionDate}
              onChange={(e) => setElectionDate(e.target.value)}
              placeholder="e.g., 2024 General"
              className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue))]"
            />
          </label>
        </div>

        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="section-label">Official source URLs</span>
            <button
              data-testid="add-url-btn"
              type="button"
              onClick={addUrl}
              className="text-xs flex items-center gap-1 text-[hsl(var(--accent-blue))] hover:underline"
            >
              <Plus className="w-3 h-3" /> add URL
            </button>
          </div>

          <div className="space-y-2">
            {urls.map((u, i) => (
              <div key={i} className="flex items-center gap-2">
                <LinkIcon className="w-3.5 h-3.5 text-[hsl(var(--ink-muted))] shrink-0" />
                <input
                  data-testid={`url-input-${i}`}
                  value={u}
                  onChange={(e) => setUrl(i, e.target.value)}
                  placeholder="https://voterguide.sos.ca.gov/propositions/..."
                  className="flex-1 bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue))]"
                />
                {urls.length > 1 && (
                  <button
                    data-testid={`url-remove-${i}`}
                    type="button"
                    onClick={() => removeUrl(i)}
                    className="text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--stamp-red))]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {SAMPLES.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setUrls([s.url])}
                data-testid={`sample-${i}`}
                className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))] px-2 py-1 border border-[hsl(var(--rule))] rounded-sm transition-colors"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <details className="mb-5 group">
          <summary className="cursor-pointer section-label inline-flex items-center gap-1.5">
            <span className="group-open:hidden">+</span>
            <span className="hidden group-open:inline">−</span>
            Paste measure text instead (fallback)
          </summary>
          <p className="mt-2 text-xs text-[hsl(var(--ink-muted))] leading-relaxed max-w-3xl">
            We could not reliably read this source? That can happen with
            scanned PDFs, blocked websites, or image-only documents. You can
            still analyze it by copying and pasting the full official text
            below.
          </p>
          <textarea
            data-testid="pasted-text"
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            placeholder="Paste the full measure text here if fetching fails..."
            rows={8}
            className="mt-2 w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue))]"
          />
        </details>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-start gap-2 text-[11px] text-[hsl(var(--ink-muted))]">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[hsl(var(--accent-amber))]" />
            <span>Analysis can take 30-90s. Sources are re-read for every run.</span>
          </div>

          <button
            data-testid="ingest-submit"
            onClick={submit}
            disabled={loading}
            className="pill-btn pill-btn-primary disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Analyzing…
              </>
            ) : (
              <>
                Analyze measure <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </section>

      <section className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="feature-strip">
        {[
          { t: "Plain-English translation", d: "Every claim carries a direct quote. No spin." },
          { t: "Before / after law comparison", d: "Proposed text vs. live California code, side-by-side." },
          { t: "Hidden provision scanner", d: "Flags delegation, carve-outs, catch-alls." },
        ].map((f, i) => (
          <div key={i} className="paper-card p-4 rounded-sm">
            <div className="section-label mb-1.5">0{i + 1}</div>
            <div className="font-serif text-lg font-semibold mb-1 leading-tight">{f.t}</div>
            <div className="text-sm text-[hsl(var(--ink-2))] leading-snug">{f.d}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
